/**
 * Adapt a backend "variation-shaped" payload (pinned_attributes +
 * available_options + compatibility_tree + node_images, as produced by
 * SchoolApiController::formatVariation / formatProductAsVariation) into the same
 * shape the product configurator (AttributeSelector, useCompatibilityTree)
 * consumes: { colors, sizes, attributes, compatibility_tree, nodeImages, … }.
 *
 * Shared by useVariation (single PDP variation) and usePackageVariation (each
 * product inside a package), so packages support every option products do.
 */
export default function adaptVariation(v) {
  if (!v) return null;

  const pinned  = Array.isArray(v.pinned_attributes) ? v.pinned_attributes : [];
  const options = Array.isArray(v.available_options) ? v.available_options : [];

  const bySlug = new Map();
  const ingest = (attr, isPinned) => {
    if (!attr?.slug) return;
    let row = bySlug.get(attr.slug);
    if (!row) {
      row = {
        id: attr.id ?? null, // 3D bindings reference attributes by id
        slug: attr.slug,
        name: attr.name,
        type: attr.type,
        logo_source: attr.logo_source ?? null,
        logo_image_url: attr.logo_image_url ?? null,
        logo_image_front_url: attr.logo_image_front_url ?? null,
        logo_image_back_url: attr.logo_image_back_url ?? null,
        regex: attr.regex ?? null,
        subtitle: attr.subtitle ?? null,
        hint: attr.hint ?? null,
        hint_html: attr.hint_html ?? null,
        option_thumbnails: !!attr.option_thumbnails,
        is_required: !!attr.is_required,
        display_only: !!attr.display_only,
        sort_order: Number.isFinite(attr.sort_order) ? attr.sort_order : 0,
        values: [],
        pinnedValueIds: new Set(),
      };
      bySlug.set(attr.slug, row);
    }
    (attr.values || []).forEach((val) => {
      row.values.push(val);
      if (isPinned) row.pinnedValueIds.add(val.id);
    });
  };
  pinned.forEach((a) => ingest(a, true));
  options.forEach((a) => ingest(a, false));

  const attributes = Array.from(bySlug.values())
    // Respect the admin's attribute sort_order across the merged pinned +
    // available groups (the BE splits them, so re-sort here). Stable on V8.
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((a) => ({
      id: a.id,
      slug: a.slug,
      name: a.name,
      type: a.type,
      logo_source: a.logo_source,
      logo_image_url: a.logo_image_url,
      logo_image_front_url: a.logo_image_front_url ?? null,
      logo_image_back_url: a.logo_image_back_url ?? null,
      regex: a.regex,
      subtitle: a.subtitle,
      hint: a.hint,
      hint_html: a.hint_html,
      option_thumbnails: a.option_thumbnails,
      is_required: a.is_required,
      display_only: a.display_only,
      sort_order: a.sort_order,
      pinned: a.pinnedValueIds.size > 0,
      pinnedValueIds: Array.from(a.pinnedValueIds),
      values: a.values,
    }));

  const colorAttr = attributes.find((a) => a.slug === 'color');
  const sizeAttr  = attributes.find((a) => a.slug === 'size');

  const colors = (colorAttr?.values ?? []).map((val) => ({
    color: val.rawValue ?? val.value,
    name:  val.value,
  }));

  return {
    id:                  v.product_id,
    variationId:         v.id,
    sku:                 v.sku,
    name:                v.name,
    slug:                v.slug,
    description:         v.description,
    shortDescription:    v.short_description,
    price:               v.price,
    priceRaw:            v.priceRaw,
    images:              Array.isArray(v.images) ? v.images : [],
    colors,
    pinnedColorValueIds: colorAttr?.pinnedValueIds ?? [],
    sizes:               (sizeAttr?.values ?? []).map((val) => val.value),
    attributes,
    collection:          v.collection ?? null,
    deliveryInformation: v.deliveryInformation ?? null,
    // Admin per-product size-guide drawer HTML — this adapter WHITELISTS
    // fields, so every new payload field must be added here explicitly.
    // Product-page layout template ('photos' | a 3D template key) + the
    // variation's attribute -> 3D mapping (null = none). See
    // Common/Product/threeD/buildThreeDProps.js.
    layout:              v.layout ?? 'photos',
    three_d:             v.three_d ?? null,
    // Resolved logo images of the school/shop/class context ({school, school_sources:
    // {uploaded, imported, brand_default}, class:{front,back,…}, class_sources:
    // {uploaded:{front,back}, brand_default}}); 3D commands with a class_logo:<key> /
    // class_logo_uploaded:<key> / brand_default_class_logo / school_logo* /
    // brand_default_logo source read them.
    logo_assets:         v.logo_assets ?? null,
    // The season year of the shop in context (else the brand's current season): the
    // Vogntrøje's 3D jersey line STUDENT <year> (buildThreeDProps `seasonYear`).
    season_year:         v.season_year ?? null,
    sizeguide_override:  v.sizeguide_override ?? null,
    compatibility_tree:  v.compatibility_tree ?? [],
    nodeImages:          v.node_images ?? {},
    // Control-visibility config (default-hidden controls + show/hide rules).
    hidden_attributes:   Array.isArray(v.hidden_attributes) ? v.hidden_attributes : [],
    visibility_rules:    Array.isArray(v.visibility_rules) ? v.visibility_rules : [],
  };
}
